import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMicOutlined";
import { getSupportConversations, getMessages, sendMessage, uploadAttachment, markRead } from "../../api/chatService";
import { formatDate } from "./constants";

const LIST_POLL_INTERVAL_MS = 5000;
const THREAD_POLL_INTERVAL_MS = 3000;

function formatRelativeTime(value) {
  if (!value) return "";
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(value).toLocaleDateString();
}

function lastMessagePreview(conversation) {
  if (!conversation.lastMessageText) {
    return conversation.lastMessageType === 2 ? "📷 Photo" : conversation.lastMessageAt ? "..." : "No messages yet";
  }
  return conversation.lastMessageText;
}

export default function Chats() {
  const [conversations, setConversations] = useState([]);
  const [listError, setListError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadError, setThreadError] = useState("");
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [sending, setSending] = useState(false);

  const listPollRef = useRef(null);
  const threadPollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadConversations = useCallback(() => {
    getSupportConversations()
      .then((data) => {
        setConversations(data);
        setListError("");
      })
      .catch(() => setListError("Could not load conversations. Is the API running?"));
  }, []);

  useEffect(() => {
    loadConversations();
    listPollRef.current = setInterval(loadConversations, LIST_POLL_INTERVAL_MS);
    return () => clearInterval(listPollRef.current);
  }, [loadConversations]);

  const loadMessages = useCallback((conversationId) => {
    return getMessages(conversationId, 1, 50).then((data) => {
      setMessages([...data.items].reverse());
    });
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;

    setThreadLoading(true);
    setThreadError("");
    loadMessages(selectedId)
      .catch(() => setThreadError("Could not load messages."))
      .finally(() => setThreadLoading(false));

    markRead(selectedId)
      .then(loadConversations)
      .catch(() => {});

    threadPollRef.current = setInterval(() => {
      loadMessages(selectedId)
        .then(() => markRead(selectedId))
        .then(loadConversations)
        .catch(() => {});
    }, THREAD_POLL_INTERVAL_MS);

    return () => clearInterval(threadPollRef.current);
  }, [selectedId, loadMessages, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const handleSelectConversation = (conversationId) => {
    setMessages([]);
    setDraft("");
    setPendingFile(null);
    setSelectedId(conversationId);
  };

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !pendingFile) || sending || !selectedId) return;

    setSending(true);
    try {
      if (pendingFile) {
        const message = await uploadAttachment(selectedId, pendingFile, text || undefined);
        setMessages((prev) => [...prev, message]);
        setPendingFile(null);
      } else {
        const message = await sendMessage(selectedId, { messageText: text });
        setMessages((prev) => [...prev, message]);
      }
      setDraft("");
      loadConversations();
    } catch {
      setThreadError("Could not send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = "";
  };

  const selectedConversation = conversations.find((c) => c.conversationId === selectedId);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Support Chats
      </Typography>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ height: "calc(100vh - 220px)", minHeight: 480 }}>
        <Paper sx={{ width: 320, flexShrink: 0, overflowY: "auto" }}>
          <List disablePadding>
            {conversations.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No support conversations yet.
                </Typography>
              </Box>
            )}
            {conversations.map((conversation) => (
              <ListItemButton
                key={conversation.conversationId}
                selected={conversation.conversationId === selectedId}
                onClick={() => handleSelectConversation(conversation.conversationId)}
                alignItems="flex-start"
              >
                <ListItemAvatar>
                  <Badge
                    color="error"
                    badgeContent={conversation.unreadCount}
                    invisible={!conversation.unreadCount}
                  >
                    <Avatar src={conversation.customerProfileImageUrl || undefined}>
                      {conversation.customerName?.[0]?.toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={conversation.customerName}
                  secondary={
                    <>
                      {conversation.customerPhone && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {conversation.customerPhone}
                        </Typography>
                      )}
                      <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {conversation.lastMessageIsMine ? "You: " : ""}
                        {lastMessagePreview(conversation)}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {formatRelativeTime(conversation.lastMessageAt)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {!selectedId ? (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <HeadsetMicIcon sx={{ fontSize: 40, color: "text.disabled" }} />
              <Typography variant="body2" color="text.secondary">
                Select a conversation to view messages
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="subtitle1">{selectedConversation?.customerName}</Typography>
                {selectedConversation?.customerPhone && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedConversation.customerPhone}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {threadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {threadError}
                  </Alert>
                )}
                {threadLoading && messages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading messages...
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {messages.map((message) => (
                      <Box
                        key={message.messageId}
                        sx={{
                          alignSelf: message.isMine ? "flex-end" : "flex-start",
                          maxWidth: "70%",
                        }}
                      >
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor: message.isMine ? "primary.main" : "grey.100",
                            color: message.isMine ? "primary.contrastText" : "text.primary",
                            borderRadius: 2,
                          }}
                        >
                          {message.isDeleted ? (
                            <Typography variant="body2" fontStyle="italic">
                              This message was deleted
                            </Typography>
                          ) : (
                            <>
                              {message.attachmentUrl && message.attachmentFileType?.startsWith("image") && (
                                <Box
                                  component="img"
                                  src={message.attachmentUrl}
                                  alt="attachment"
                                  sx={{ maxWidth: 220, borderRadius: 1, mb: message.messageText ? 1 : 0, display: "block" }}
                                />
                              )}
                              {message.messageText && <Typography variant="body2">{message.messageText}</Typography>}
                            </>
                          )}
                        </Paper>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {formatDate(message.sentAt)}
                        </Typography>
                      </Box>
                    ))}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
                {pendingFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {pendingFile.name} ready to send
                  </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <IconButton onClick={() => fileInputRef.current?.click()} disabled={sending}>
                    <ImageIcon />
                  </IconButton>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder={pendingFile ? "Add a caption..." : "Type a message..."}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={sending || (!draft.trim() && !pendingFile)}
                  >
                    <SendIcon />
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
